
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { logger } from '@/utils/logger';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Helper function to validate and assign municipality for a user
    const validateAndAssignMunicipality = async (
      userId: string, 
      userEmail: string | undefined, 
      municipalityId: string
    ): Promise<boolean> => {
      logger.info('=== VALIDATING MUNICIPALITY ACCESS ===', { 
        municipalityId, 
        userEmail,
        municipalityIdType: typeof municipalityId,
        isValidId: !!municipalityId && municipalityId !== 'undefined' && municipalityId !== 'null'
      });
      
      // Check if user has explicit permission in allowed_emails
      const { data: allowedEmail } = await supabase
        .from('allowed_emails')
        .select('municipality_id')
        .eq('email', userEmail)
        .maybeSingle();
      
      let hasPermission = false;
      
      if (allowedEmail && allowedEmail.municipality_id !== null) {
        // User is in allowed_emails with a specific municipality assigned
        if (allowedEmail.municipality_id === municipalityId) {
          hasPermission = true;
          logger.info('✅ User has explicit permission for this municipality');
        } else {
          logger.warn('❌ User tried to access wrong municipality', {
            allowed: allowedEmail.municipality_id,
            attempted: municipalityId
          });
          return false;
        }
      } else {
        // User not in allowed_emails OR has null municipality_id
        // In both cases, check if the municipality has open registration
        logger.info('=== CHECKING OPEN REGISTRATION ===', {
          municipalityId,
          municipalityIdType: typeof municipalityId,
          userEmail
        });
        
        const { data: accessControl, error: accessControlError } = await supabase
          .from('site_access_control')
          .select('is_open_registration, municipality_id')
          .eq('municipality_id', municipalityId)
          .maybeSingle();
        
        logger.info('=== ACCESS CONTROL QUERY RESULT ===', {
          data: accessControl,
          error: accessControlError,
          isOpenRegistration: accessControl?.is_open_registration
        });
        
        if (accessControl?.is_open_registration) {
          hasPermission = true;
          logger.info('✅ Open registration enabled for this municipality');
        } else {
          logger.warn('❌ User not authorized and open registration disabled', {
            accessControlExists: !!accessControl,
            isOpenRegistration: accessControl?.is_open_registration,
            municipalityId
          });
          return false;
        }
      }
      
      // If permission granted, update profile
      if (hasPermission) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ municipality_id: municipalityId })
          .eq('id', userId);
        
        if (updateError) {
          console.error('Error updating municipality:', updateError);
          return false;
        }
        
        console.log('Municipality assigned successfully');
        return true;
      }
      
      return false;
    };

    const handleCallback = async () => {
      try {
        logger.info('=== AUTH CALLBACK STARTED ===');
        
        // Get municipality_id from URL or localStorage FIRST
        const municipalityIdFromUrl = searchParams.get('municipality_id');
        const municipalityIdFromStorage = localStorage.getItem('selected_municipality_id');
        
        logger.info('Municipality ID sources:', {
          fromUrl: municipalityIdFromUrl,
          fromStorage: municipalityIdFromStorage,
          urlSearchParams: window.location.search,
          allSearchParams: Object.fromEntries(searchParams.entries())
        });
        
        const municipalityId = municipalityIdFromUrl || municipalityIdFromStorage;
        
        // Validate municipality ID
        if (!municipalityId || municipalityId === 'undefined' || municipalityId === 'null') {
          logger.error('Invalid or missing municipality ID', {
            municipalityId,
            type: typeof municipalityId
          });
          navigate('/login?error=no_municipality', { replace: true });
          return;
        }
        
        logger.info('Using municipality ID:', municipalityId);
        
        // SECURITY: Check if user is already authenticated
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        
        if (existingSession) {
          logger.info('User already authenticated - validating municipality access', {
            userId: existingSession.user.id,
            email: existingSession.user.email
          });
          
          // CRITICAL SECURITY: Validate that existing user has access to selected municipality
          const { data: profile } = await supabase
            .from('profiles')
            .select('municipality_id')
            .eq('id', existingSession.user.id)
            .single();
          
          logger.info('User profile loaded:', profile);
          
          // Check if user is super admin
          const { data: isSuperAdmin } = await supabase.rpc('is_super_admin', {
            user_id_param: existingSession.user.id
          });
          
          // Super admins can access any municipality - grant access immediately
          if (isSuperAdmin) {
            logger.info('✅ Super admin access granted - can access any municipality');
            localStorage.removeItem('selected_municipality_id');
            navigate('/');
            return;
          }
          
          if (profile?.municipality_id) {
            // Regular users must match their assigned municipality
            if (profile.municipality_id !== municipalityId) {
              // Trying to access wrong municipality
              logger.error('SECURITY: Existing user attempted to access unauthorized municipality', {
                user_municipality: profile.municipality_id,
                attempted_municipality: municipalityId,
                user_email: existingSession.user.email
              });
              
              await supabase.auth.signOut({ scope: 'local' });
              localStorage.removeItem('selected_municipality_id');
              navigate('/login?error=unauthorized_municipality', { replace: true });
              return;
            }
            
            // Municipality matches, allow access
            logger.info('Existing user validated for correct municipality');
            localStorage.removeItem('selected_municipality_id');
            navigate('/', { replace: true });
            return;
          } else {
            // User has null municipality_id - must go through validation
            logger.info('User has no municipality assigned, validating access...');
            
            const hasAccess = await validateAndAssignMunicipality(
              existingSession.user.id,
              existingSession.user.email,
              municipalityId
            );
            
            if (hasAccess) {
              logger.info('✅ ACCESS GRANTED - Municipality assigned successfully');
              localStorage.removeItem('selected_municipality_id');
              navigate('/', { replace: true });
              return;
            } else {
              // Access denied
              logger.error('❌ ACCESS DENIED - User does not have permission for this municipality');
              await supabase.auth.signOut({ scope: 'local' });
              localStorage.removeItem('selected_municipality_id');
              navigate('/login?error=unauthorized_municipality', { replace: true });
              return;
            }
          }
        }

        // Check for auth errors in URL
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          logger.warn('Auth error from URL:', { error, errorDescription });
          
          // Clear corrupt session data on auth errors
          logger.info('Clearing potentially corrupt session data...');
          localStorage.removeItem('supabase.auth.token');
          await supabase.auth.signOut({ scope: 'local' });
          
          // Decode URL-encoded error message
          const decodedError = decodeURIComponent(errorDescription || '');
          
          // Check for specific user registration error or database error
          if (decodedError.includes('user_registration_closed::') || 
              decodedError.includes('registration_closed') ||
              (error === 'server_error' && decodedError.includes('Database error saving new user'))) {
            navigate('/login?error=registration_closed', { replace: true });
            return;
          }
          
          // Other types of errors
          navigate('/login?error=general', { replace: true });
          return;
        }

        // Get the code from URL search params
        const code = searchParams.get('code');
        
        if (!code) {
          logger.info('No code and no existing session, redirecting to login');
          navigate('/login', { replace: true });
          return;
        }

        // Basic validation of code
        if (!code.trim()) {
          logger.error('Empty or invalid auth code');
          navigate('/login?error=invalid_code', { replace: true });
          return;
        }

        logger.info('Exchanging code for session...');
        // Exchange code for session only if we don't have an existing session
        const { data: sessionData, error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
        
        if (sessionError) {
          logger.error('Session error:', sessionError);
          
          // For PKCE errors, don't sign out if we might have had a valid session
          if (sessionError.message?.includes('auth code and code verifier should be non-empty') ||
              sessionError.message?.includes('invalid request')) {
            logger.error('PKCE validation failed');
            navigate('/login?error=pkce_failed', { replace: true });
            return;
          }
          
          // Check for registration error here too
          if (sessionError.message?.includes('user_registration_closed::') || 
              sessionError.message?.includes('registration_closed')) {
            navigate('/login?error=registration_closed', { replace: true });
            return;
          }
          
          navigate('/login?error=general', { replace: true });
          return;
        }

        // Update municipality for new user
        if (sessionData?.user) {
          logger.info('New user session created, validating municipality access...');
          
          const hasAccess = await validateAndAssignMunicipality(
            sessionData.user.id,
            sessionData.user.email,
            municipalityId
          );
          
          if (!hasAccess) {
            logger.error('❌ ACCESS DENIED - New user unauthorized for municipality');
            await supabase.auth.signOut({ scope: 'local' });
            navigate('/login?error=unauthorized_municipality', { replace: true });
            return;
          }
          
          logger.info('✅ NEW USER ACCESS GRANTED');
          localStorage.removeItem('selected_municipality_id');
        }

        logger.info('Session established, redirecting to home');
        // Redirect to home page on success
        navigate('/', { replace: true });
        
      } catch (err) {
        logger.error('Auth callback error:', err);
        
        const errorMessage = err instanceof Error ? err.message : 'Ett fel uppstod vid inloggning';
        setError(errorMessage);
        navigate('/login?error=general', { replace: true });
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Inloggningsfel</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <LoadingSpinner />
    </div>
  );
};

export default AuthCallback;
